import { Descriptions, Drawer, Empty, Grid, Skeleton, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';

import { EDateFormat } from '../fragments/date-format';
import { type ICustomerDetailEmailPreview } from '../reducers/crm-customer-detail/types';
import { COLOR, SPACING } from '../theme/tokens';

const { Paragraph } = Typography;

interface IEmailPreviewDrawerProps {
  open: boolean;
  inProcess: boolean;
  preview: ICustomerDetailEmailPreview | null;
  onClose: () => void;
}

const renderPreviewBody = (
  preview: ICustomerDetailEmailPreview,
): React.ReactElement => {
  if (preview.html) {
    return (
      <iframe
        title="Email preview"
        sandbox=""
        srcDoc={preview.html}
        style={{
          width: '100%',
          height: 'calc(100vh - 16rem)',
          border: `1px solid ${COLOR.borderDefault}`,
          borderRadius: 6,
          backgroundColor: COLOR.emailCanvas,
        }}
      />
    );
  }

  if (preview.text) {
    return (
      <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{preview.text}</Paragraph>
    );
  }

  return <Empty description="Email has no content" />;
};

export function EmailPreviewDrawer({
  open,
  inProcess,
  preview,
  onClose,
}: IEmailPreviewDrawerProps): React.ReactElement {
  const { md: isDesktop } = Grid.useBreakpoint();

  const renderContent = (): React.ReactElement => {
    if (inProcess) {
      return <Skeleton active />;
    }

    if (!preview) {
      return <Empty description="Email content not found" />;
    }

    return (
      <>
        <Descriptions
          bordered
          size="small"
          column={1}
          style={{ marginBottom: SPACING.lg }}
          items={[
            {
              key: 'from',
              label: 'From',
              children: preview.from,
            },
            {
              key: 'sent',
              label: 'Sent',
              children: dayjs(preview.createdAt).format(EDateFormat.DATE_TIME),
            },
          ]}
        />
        {renderPreviewBody(preview)}
      </>
    );
  };

  return (
    <Drawer
      title={preview?.subject || 'Email preview'}
      placement="right"
      open={open}
      onClose={onClose}
      destroyOnHidden
      styles={{ wrapper: { width: isDesktop ? '60%' : '90%' } }}
    >
      {renderContent()}
    </Drawer>
  );
}
